import { useState } from 'react';
import type { ConnectionStatus } from '@tcg/shared';
import { api } from '../lib/api';
import { Badge, Button, Card, CardBody, CardHeader, Spinner } from './ui';
import { IconPlug } from './icons';

const STATE_TONE = {
  connected: 'green',
  not_configured: 'slate',
  disconnected: 'amber',
  reconnect_needed: 'red',
} as const;

const STATE_LABEL = {
  connected: 'Connected',
  not_configured: 'Not configured',
  disconnected: 'Not connected',
  reconnect_needed: 'Reconnect needed',
} as const;

export function ConnectionCard({ status, onChange }: { status: ConnectionStatus; onChange: () => void }) {
  const [busy, setBusy] = useState(false);

  const connect = () => {
    // Browser-driven OAuth redirect (per-user authorization_code).
    window.location.href = api.serviceChannelConnectUrl();
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.disconnectServiceChannel();
      onChange();
    } finally {
      setBusy(false);
    }
  };

  const tone = STATE_TONE[status.state];
  const showConnect = status.perUser && (status.state === 'disconnected' || status.state === 'reconnect_needed');
  const showDisconnect = status.perUser && status.state === 'connected';

  const DOT: Record<string, string> = {
    green: 'bg-emerald-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
    slate: 'bg-slate-300',
  };

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
              <IconPlug className="h-4 w-4" />
            </span>
            {status.label}
          </span>
        }
        subtitle={status.perUser ? 'Per-user OAuth connection' : 'App-level credentials'}
        action={
          <Badge tone={tone}>
            <span className={`h-1.5 w-1.5 rounded-full ${DOT[tone]}`} aria-hidden />
            {STATE_LABEL[status.state]}
          </Badge>
        }
      />
      <CardBody className="flex items-center justify-between gap-4">
        <p className="text-sm text-slate-500">{status.detail ?? 'Ready.'}</p>
        <div className="flex shrink-0 gap-2">
          {showConnect && (
            <Button onClick={connect} variant="primary">
              {status.state === 'reconnect_needed' ? 'Reconnect' : 'Connect'}
            </Button>
          )}
          {showDisconnect && (
            <Button onClick={disconnect} variant="secondary" disabled={busy}>
              {busy ? <Spinner /> : 'Disconnect'}
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
